import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./Select.css";
import { FontAwesomeIcons } from "../../core/Types";
import Icon from "../base/Icon";

// A fully native-React replacement for <DropDown /> (which is backed by select2).
// It matches DropDown's label/error/box look, supports type-to-search inside the
// panel, and carries the commonly used DropDown props. No external UI/select libs.
interface SelectProps {
    id?: string
    name?: string
    label?: string
    labelUp?: boolean
    labelClass?: string
    icon?: FontAwesomeIcons
    iconColor?: string
    small?: boolean
    solid?: boolean
    block?: boolean
    placeholder?: string
    required?: boolean
    disabled?: boolean
    marginBottom?: number
    width?: number
    /**
     * Minimum width of the open panel, in px. The box keeps its own width; the panel grows past
     * it so rich options (a technician with badges, availability and distance) are not cut off.
     * When the wider panel would leave the screen on the right it grows to the left instead.
     */
    panelWidth?: number
    visible?: boolean
    tabIndex?: number

    source?: any[]
    dataText?: string
    dataValue?: string
    dataColor?: string
    dataGroup?: string

    // defaultValue seeds the selection once on load; value is controlled and
    // updates the selection whenever it changes; reset clears it.
    defaultValue?: string
    value?: string
    reset?: boolean
    /**
     * Whether the chosen value can be cleared. On by default: most selects answer an optional
     * question, and one that has been answered by accident was impossible to unanswer -- the
     * panel offers no empty option, so the only way back was to reload the form.
     *
     * Pass false where a blank is not a valid answer: a country, a number type, a billing period.
     */
    deselect?: boolean

    // Multi-select: the box shows chips, the panel stays open while picking, and
    // `values`/`defaultValues` are used instead of `value`/`defaultValue`.
    multiple?: boolean
    values?: string[]
    defaultValues?: string[]
    onChange?: (values: string[]) => void

    searchable?: boolean
    searchPlaceholder?: string
    // What typing in the panel matches against. Defaults to the option's dataText, which is
    // all there is to match when the row shows only that; a row that renders more than the
    // text -- a region, a source -- should let those be searched too.
    searchText?: (item: any) => string

    // Custom renderers. optionTemplate customizes each option in the panel;
    // selectionTemplate customizes the selected value shown in the closed box.
    optionTemplate?: (item: any) => React.ReactNode
    selectionTemplate?: (item: any) => React.ReactNode

    onSelect?: (val?: string, text?: string) => void
    onSelectItem?: (item: any) => void
    onClear?: () => void
    onDeselect?: () => void
}

// The left and right edges of the nearest ancestor that clips its overflow sideways, or the
// window's when nothing does.
function clippingBounds(el: HTMLElement): { left: number, right: number } {
    for (let p = el.parentElement; p; p = p.parentElement) {
        const overflowX = getComputedStyle(p).overflowX;
        if (overflowX !== "visible") {
            const r = p.getBoundingClientRect();
            return { left: r.left, right: r.right };
        }
    }
    return { left: 0, right: window.innerWidth };
}

export default function Select(props: SelectProps) {

    const dataText = props.dataText ?? "name";
    const dataValue = props.dataValue ?? "id";
    const dataColor = props.dataColor;
    const dataGroup = props.dataGroup;
    const searchable = props.searchable ?? true;
    const placeholder = props.placeholder ?? "Choose One";

    const inputId = useMemo(() => props.id ?? "sel-" + Math.floor(Math.random() * 1000000), [props.id]);

    const multiple = props.multiple ?? false;

    const [open, openSet] = useState<boolean>(false);
    const [keyword, keywordSet] = useState<string>("");

    // The option the keyboard is on. Searching puts it back on the first result, so typing
    // "ca" and pressing Enter picks California without touching the arrow keys.
    const [activeIndex, activeIndexSet] = useState<number>(0);
    const [value, valueSet] = useState<string | undefined>(props.value ?? props.defaultValue);
    const [values, valuesSet] = useState<string[]>(props.values ?? props.defaultValues ?? []);

    // The panel normally hangs below the box; near the bottom of the viewport it flips above it.
    const [dropUp, dropUpSet] = useState<boolean>(false);
    // A panel wider than the box (panelWidth) hangs from the box's right edge when there is no
    // room for it to the right.
    const [alignRight, alignRightSet] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const firstValueRun = useRef<boolean>(true);
    const firstValuesRun = useRef<boolean>(true);

    // `value` is controlled: update the selection on every change. The initial
    // mount is skipped so `defaultValue` (initial-only) isn't clobbered when the
    // component is used uncontrolled.
    useEffect(() => {
        if (firstValueRun.current) { firstValueRun.current = false; return; }
        valueSet(props.value || undefined);
    }, [props.value]);

    useEffect(() => {
        if (firstValuesRun.current) { firstValuesRun.current = false; return; }
        valuesSet(props.values ?? []);
    }, [props.values]);

    // External reset clears the selection.
    useEffect(() => {
        if (props.reset) { valueSet(undefined); valuesSet([]); }
    }, [props.reset]);

    const selectedItem = props.source?.find(i => `${i[dataValue]}` === `${value}`);
    const selectedItems = (props.source ?? []).filter(i => values.includes(`${i[dataValue]}`));

    const filtered = (props.source ?? []).filter(i => {
        // Multi-select: a picked item lives in the chips, so it leaves the menu — and comes
        // back the moment its chip is removed. Single-select keeps its selection listed so
        // the current choice stays visible (and highlighted) when the menu is reopened.
        if (multiple && values.includes(`${i[dataValue]}`)) return false;

        if (!keyword.trim()) return true;
        // In a grouped list the group's name is searched too, so typing a state finds every
        // area code in it.
        const haystack = props.searchText ? props.searchText(i)
            : `${i[dataText] ?? ""}` + (dataGroup ? ` ${i[dataGroup] ?? ""}` : "");
        return haystack.toLowerCase().includes(keyword.trim().toLowerCase());
    });

    // Preserve the source order of groups as they first appear.
    const groupNames: string[] = [];
    if (dataGroup) {
        filtered.forEach(i => {
            const g = `${i[dataGroup] ?? ""}`;
            if (!groupNames.includes(g)) groupNames.push(g);
        });
    }

    // The options in the order they are actually drawn, so keyboard navigation and the list
    // on screen agree even when the items are grouped.
    const flatOptions = dataGroup
        ? groupNames.flatMap(g => filtered.filter(i => `${i[dataGroup] ?? ""}` === g))
        : filtered;

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) openSet(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [open]);

    // Focus the search field when opening; clear the keyword when closing.
    useEffect(() => {
        if (open && searchable) setTimeout(() => searchRef.current?.focus(), 0);
        if (!open) keywordSet("");
    }, [open]);

    // Every new search starts from its first result.
    useEffect(() => { activeIndexSet(0); }, [keyword]);

    // Opening starts on whatever is already picked, so Enter straight after opening keeps
    // the current value instead of jumping to the top of the list.
    useEffect(() => {
        if (!open) return;
        const idx = flatOptions.findIndex(i => `${i[dataValue]}` === `${value}`);
        activeIndexSet(idx >= 0 ? idx : 0);
    }, [open]);

    // Keep the keyboard's option inside the scroll box as it moves.
    useLayoutEffect(() => {
        if (!open) return;

        const box = optionsRef.current;
        const target = box?.querySelector<HTMLElement>('[data-active="true"]');
        if (!box || !target) return;

        if (target.offsetTop < box.scrollTop) box.scrollTop = target.offsetTop;
        else if (target.offsetTop + target.offsetHeight > box.scrollTop + box.clientHeight) {
            box.scrollTop = target.offsetTop + target.offsetHeight - box.clientHeight;
        }
    }, [activeIndex, open]);

    // Flip the panel above the box when there isn't room for it below — a select at the
    // bottom of a page or a drawer would otherwise open into the fold. Runs before paint
    // so the panel never appears in the wrong place first.
    // The decision is made from the panel's own height, which doesn't change when it flips,
    // so this settles in one pass instead of bouncing between the two sides.
    useLayoutEffect(() => {
        if (!open) { dropUpSet(false); alignRightSet(false); return; }

        const measure = () => {
            const box = containerRef.current;
            if (!box) return;

            const rect = box.getBoundingClientRect();
            const panelHeight = panelRef.current?.offsetHeight ?? 260;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // Only flip when it genuinely doesn't fit below AND there is more room above,
            // so a select that fits nowhere still opens downwards like every other one.
            dropUpSet(spaceBelow < panelHeight + 8 && spaceAbove > spaceBelow);

            // Same idea sideways, for a panel wider than the box. Its width is the same on
            // either side, so this settles in one pass too.
            // The room ends at whatever clips the panel first -- a drawer or a modal body, not
            // just the window -- or the panel would widen that box and scroll it sideways.
            if (props.panelWidth) {
                const panelWidth = panelRef.current?.offsetWidth ?? props.panelWidth;
                const bounds = clippingBounds(box);
                alignRightSet(rect.left + panelWidth > bounds.right - 8 && rect.right - panelWidth >= bounds.left + 8);
            }
        };

        measure();

        // Capture phase: the box can be inside a scrolling drawer or modal, not just the window.
        window.addEventListener("scroll", measure, true);
        window.addEventListener("resize", measure);
        return () => {
            window.removeEventListener("scroll", measure, true);
            window.removeEventListener("resize", measure);
        };
        // The panel grows and shrinks as the search narrows the list, so re-measure with it.
    }, [open, filtered.length, searchable]);

    // Bring the current selection into view when the panel opens, so picking the 40th item
    // and reopening doesn't start at the top of the list again. Only on open: once the user
    // is typing a search, the top of the results is where they want to be.
    // scrollTop rather than scrollIntoView() — the latter also scrolls the page or the drawer
    // the select happens to be sitting in.
    useLayoutEffect(() => {
        if (!open) return;

        const box = optionsRef.current;
        if (!box) return;

        const target = box.querySelector<HTMLElement>('[data-selected="true"]');
        if (!target) return;

        // Centre it when the list is long enough to scroll; the clamp keeps selections near
        // either end from leaving a gap.
        const wanted = target.offsetTop - (box.clientHeight / 2) + (target.offsetHeight / 2);
        box.scrollTop = Math.max(0, Math.min(wanted, box.scrollHeight - box.clientHeight));
    }, [open]);

    const choose = (item: any) => {
        const v = `${item[dataValue]}`;

        if (multiple) {
            const next = values.includes(v) ? values.filter(x => x !== v) : [...values, v];
            valuesSet(next);
            props.onChange?.(next);
            props.onSelectItem?.(item);
        }
        else {
            valueSet(v);
            openSet(false);
            props.onSelect?.(v, item[dataText]);
            props.onSelectItem?.(item);
        }

        if (props.name && typeof window.hideFormError === "function") window.hideFormError(props.name);
    };

    const removeValue = (v: string) => {
        const next = values.filter(x => x !== v);
        valuesSet(next);
        props.onChange?.(next);
    };

    const clear = () => {
        valueSet(undefined);
        valuesSet([]);
        if (multiple) props.onChange?.([]);

        // Clearing is a selection change like any other, so whoever listens for one hears it.
        // Without this a caller wired only to onSelect kept the old value in its own state while
        // the box showed empty -- which is most of them, and would have been a new bug the moment
        // deselect became the default rather than something a screen opted into.
        if (!multiple) {
            props.onSelect?.(undefined, undefined);
            props.onSelectItem?.(undefined);
        }

        props.onClear?.();
        props.onDeselect?.();
    };

    const toggle = () => {
        if (props.disabled) return;
        openSet(o => !o);
    };

    // One handler for the box and for the search field. While the panel is open it owns
    // Enter completely: without that, Enter inside the search field reaches the surrounding
    // form and submits it in the middle of picking an option.
    const keyDown = (e: React.KeyboardEvent) => {

        if (props.disabled) return;

        if (e.key === "Escape") {
            e.preventDefault();
            openSet(false);
            return;
        }

        if (!open) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                openSet(true);
            }
            return;
        }

        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (flatOptions.length === 0) return;
            const step = e.key === "ArrowDown" ? 1 : -1;
            activeIndexSet(i => (i + step + flatOptions.length) % flatOptions.length);
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            const item = flatOptions[activeIndex];
            if (item) choose(item);
            else openSet(false);
            return;
        }

        if (e.key === "Tab") openSet(false);
    };

    const fontClass = () => (props.small ? "fs-8" : "fs-7");

    const controlClass = () => {
        var c = "form-select form-inputs native-select-control";
        if (multiple) c += " native-select-multi";
        if (props.small) c += " form-select-sm";
        if (props.solid) c += " form-select-solid";
        if (props.disabled) c += " native-select-disabled";
        if (open) c += " native-select-focused";
        return c;
    };

    const renderOption = (item: any, k: number | string, flatIndex: number) => {
        const v = `${item[dataValue]}`;
        const isSelected = multiple ? values.includes(v) : v === `${value}`;
        const isActive = flatIndex === activeIndex;
        return (
            <div key={k}
                data-selected={isSelected ? "true" : undefined}
                data-active={isActive ? "true" : undefined}
                className={"native-select-option" + (isSelected ? " selected" : "") + (isActive ? " active" : "")}
                onMouseEnter={() => activeIndexSet(flatIndex)}
                onClick={() => choose(item)}>
                <span className="native-select-option-text">
                    {
                        props.optionTemplate ? props.optionTemplate(item) :
                            <>
                                {
                                    dataColor && item[dataColor] &&
                                    <span className="native-select-dot" style={{ backgroundColor: item[dataColor] }} />
                                }
                                {item[dataText]}
                            </>
                    }
                </span>
                {
                    isSelected &&
                    <Icon icon="check" type="solid" size={12} className="native-select-check" />
                }
            </div>
        );
    };

    if (props.visible !== undefined && !props.visible) return <></>;

    const control = (
        <div ref={containerRef} className="native-select" style={{ width: props.width }}>

            <div id={inputId}
                tabIndex={props.disabled ? -1 : (props.tabIndex ?? 0)}
                className={controlClass()}
                onClick={toggle}
                onKeyDown={keyDown}>

                {
                    multiple ?
                        (
                            selectedItems.length > 0 ?
                                <span className="native-select-chips">
                                    {
                                        selectedItems.map((item, k) => {
                                            const v = `${item[dataValue]}`;
                                            return (
                                                <span key={k} className="native-select-chip">
                                                    {
                                                        dataColor && item[dataColor] &&
                                                        <span className="native-select-dot" style={{ backgroundColor: item[dataColor] }} />
                                                    }
                                                    {item[dataText]}
                                                    {
                                                        !props.disabled &&
                                                        <span className="native-select-chip-x" title="Remove"
                                                            onClick={e => { e.stopPropagation(); removeValue(v); }}>
                                                            &times;
                                                        </span>
                                                    }
                                                </span>
                                            );
                                        })
                                    }
                                </span>
                                :
                                <span className="native-select-placeholder">{placeholder}</span>
                        )
                        :
                        selectedItem ?
                            <span className="native-select-value">
                                {
                                    props.selectionTemplate ? props.selectionTemplate(selectedItem) :
                                        <>
                                            {
                                                dataColor && selectedItem[dataColor] &&
                                                <span className="native-select-dot" style={{ backgroundColor: selectedItem[dataColor] }} />
                                            }
                                            {selectedItem[dataText]}
                                        </>
                                }
                            </span>
                            :
                            <span className="native-select-placeholder">{placeholder}</span>
                }

                {
                    (props.deselect ?? true) && (multiple ? selectedItems.length > 0 : !!selectedItem) && !props.disabled &&
                    <span className="native-select-clear" title="Clear"
                        onClick={e => { e.stopPropagation(); clear(); }}>
                        &times;
                    </span>
                }
            </div>

            {
                // Both modes submit with the surrounding Form, the way the select2-backed
                // DropDown did: multi as a comma-separated list (the shape the tagify posted),
                // single as the chosen value. Without this a single Select posted nothing, so a
                // required-field check reading FormData failed even with a value on screen.
                props.name &&
                <input type="hidden" name={props.name} value={multiple ? values.join(",") : (value ?? "")} />
            }

            {
                open &&
                <div ref={panelRef}
                    className={"native-select-panel" + (dropUp ? " native-select-panel-up" : "") + (alignRight ? " native-select-panel-end" : "")}
                    style={props.panelWidth ? { minWidth: props.panelWidth } : undefined}>
                    {
                        searchable &&
                        <div className="native-select-search">
                            <input ref={searchRef} type="text" autoComplete="one-time-code"
                                className="form-control form-control-sm"
                                placeholder={props.searchPlaceholder ?? "Search..."}
                                value={keyword}
                                onClick={e => e.stopPropagation()}
                                onKeyDown={keyDown}
                                onChange={e => keywordSet(e.target.value)} />
                        </div>
                    }

                    <div ref={optionsRef} className="native-select-options">
                        {
                            filtered.length === 0 ?
                                <div className="native-select-empty">
                                    {
                                        // An emptied multi-select menu usually means everything is
                                        // already picked, which "No results found" reads as an error.
                                        multiple && !keyword.trim() && selectedItems.length > 0
                                            ? "All options selected"
                                            : "No results found"
                                    }
                                </div>
                                : dataGroup ?
                                    groupNames.map((g, gi) =>
                                        <div key={`grp-${gi}`}>
                                            {g && <div className="native-select-group">{g}</div>}
                                            {
                                                filtered
                                                    .filter(i => `${i[dataGroup] ?? ""}` === g)
                                                    .map((item, k) => renderOption(item, `${gi}-${k}`, flatOptions.indexOf(item)))
                                            }
                                        </div>
                                    )
                                    :
                                    filtered.map((item, k) => renderOption(item, k, k))
                        }
                    </div>
                </div>
            }
        </div>
    );

    // Same label/error scaffolding as DropDown so it drops in identically.
    return (
        <div className={"row align-items-center mb-" + (props.marginBottom ?? 5)}>
            {
                props.label &&
                <label className={`col col-12 ${(props.labelUp ? "mb-1" : (props.block ? "col-md-3" : "col-md-2"))} ${fontClass()} ${props.labelClass ?? ""}`}
                    htmlFor={inputId}>
                    {
                        props.icon &&
                        <Icon icon={props.icon} color={props.iconColor} type="solid" className="me-1" />
                    }
                    {props.label}
                    {
                        props.labelUp && <span id={props.name + "-err"} className="small text-danger mx-3 form-error"></span>
                    }
                </label>
            }

            <div className={"col col-12 " + (props.labelUp ? "" : (props.block && props.label ? "col-md-9" : props.block ? "col-md-12" : "col-md-6"))}>
                {control}
            </div>

            {
                !props.labelUp &&
                <div className={"col col-12 " + (props.block ? "col-md-9" : "col-md-4")}>
                    <span id={props.name + "-err"} className="small text-danger mx-3 form-error"></span>
                </div>
            }
        </div>
    );
}
